<template>
  <div class="photo">
    <div class="avatar-section">
      <slot name="header"></slot>
      <RouterLink
        :to="{ name: 'profile-username', params: { username: photo.username } }"
        v-if="!hideHeader"
        class="flex items-center gap-2"
        :aria-label="$t('photo.view_profile', { username: photo.username })"
      >
        <Avatar :avatar="photo.avatar" size="xs" />
        <div class="user-info">
          <h2 class="text-stone-900 dark:text-stone-100 font-bold text-lg">{{ photo.username }}</h2>
          <div class="flex gap-5 items-center">
            <span
              class="dark:text-stone-400 text-stone-700 text-sm font-semibold"
              aria-label="Prompt word"
              >{{ photo.word }}</span
            >
            <time class="dark:text-stone-400 text-stone-700 text-xs" :datetime="photo.created_at">
              {{
                Intl.DateTimeFormat($t('locale'), { dateStyle: 'medium' }).format(
                  new Date(photo.created_at)
                )
              }}
            </time>
          </div>
        </div>
      </RouterLink>
    </div>
    <div class="photo-section">
      <ImageLoader
        :src="photo.url"
        :alt="photo.description"
        loading="eager"
        image-class="object-contain"
      />
    </div>
    <div class="interaction-section">
      <!-- <div v-if="photo.description" class="px-4 py-2 text-sm text-stone-200">
        <p>
          <span class="dark:text-stone-300 text-stone-700"> {{ photo.description }}</span>
        </p>
      </div> -->
      <div class="flex items-center gap-3 p-2">
        <button
          @click="emit('like', photo.photo_id)"
          :title="!photo.liked ? $t('photo.like_photo_disabled_title') : ''"
          :aria-label="$t('photo.like_photo_label')"
          :aria-pressed="photo.liked"
        >
          <HeartIcon :liked="photo.liked">
            <span class="text-sm">{{ photo.likes }}</span>
          </HeartIcon>
        </button>

        <button
          @click="emit('bookmark', photo.photo_id)"
          class="flex items-center transition duration-150"
          :class="{
            'text-green-500': photo.bookmarked,
            'text-stone-400': !photo.bookmarked
          }"
          :aria-label="$t('photo.bookmark_photo_label')"
          :aria-pressed="photo.bookmarked"
        >
          <svg
            v-if="!photo.bookmarked"
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            class="w-6 h-6"
          >
            <path
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m19 21l-7-4l-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
            />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            class="w-6 h-6"
          >
            <path
              fill="currentColor"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m19 21l-7-4l-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
            />
          </svg>
        </button>

        <!-- Comments button (only visible to friends or own photo) -->
        <button
          v-if="
            supabase.user.status === 'SignedIn' &&
            (photo.user_id === supabase.user.data?.id || isFriends)
          "
          @click="handleOpenDrawer"
          class="flex items-center gap-1 transition duration-150 text-stone-400 hover:text-stone-600 dark:hover:text-stone-200"
          :aria-label="$t('photo.view_comments')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            class="w-6 h-6"
          >
            <!-- Icon from Lucide by Lucide Contributors - https://github.com/lucide-icons/lucide/blob/main/LICENSE -->
            <path
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092a10 10 0 1 0-4.777-4.719"
            />
          </svg>
          <span class="text-sm">{{ photo.comments_count || 0 }}</span>
        </button>
        <TrophyIcon
          :trophyType="photo.trophy_type"
          v-if="photo.trophy_type"
          :aria-label="$t('photo.trophy_label')"
        />
        <div class="flex-grow flex items-center justify-end gap-2">
          <!-- Report button (only visible if not own photo and user is signed in) -->
          <button
            v-if="supabase.user.status === 'SignedIn' && photo.user_id !== supabase.user.data?.id"
            @click="showReportModal = true"
            class="text-stone-400 hover:text-red-500 transition duration-150"
            :aria-label="$t('photo.report_photo')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              class="w-6 h-6"
            >
              <path
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 15s1-1 4-1s5 2 8 2s4-1 4-1V3s-1 1-4 1s-5-2-8-2s-4 1-4 1zM4 22v-7"
              />
            </svg>
          </button>

          <button class="text-stone-400" @click="share">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              class="w-6 h-6"
            >
              <path
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 1 1 0-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 1 1 5.367-2.684 3 3 0 0 1-5.367 2.684zm0 9.316a3 3 0 1 1 5.367 2.684 3 3 0 0 1-5.367-2.684z"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Comments Drawer -->
    <CommentsDrawer
      :isOpen="showCommentsDrawer"
      :photoId="photo.photo_id"
      :photoOwnerId="photo.user_id"
      @close="handleCloseDrawer"
      @commentAdded="handleCommentAdded"
      @commentCountChanged="handleCommentCountChanged"
    />

    <!-- Photo Report Modal -->
    <PhotoReportModal
      :isOpen="showReportModal"
      :photo="photo"
      @close="showReportModal = false"
      @reported="handlePhotoReported"
    />
  </div>
</template>

<script setup lang="ts">
import type { Photo } from '@/stores/photos'
import { usePhotos } from '@/stores/photos'
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import TrophyIcon from '@/components/Icons/TrophyIcon.vue'
import HeartIcon from '@/components/Icons/HeartIcon.vue'
import CommentsDrawer from '@/components/CommentsDrawer.vue'
import PhotoReportModal from '@/components/PhotoReportModal.vue'
import ImageLoader from '@/components/ImageLoader.vue'
import { useSupabase } from '@/stores/supabase'
import Avatar from '@/components/Avatar.vue'
import { useFriends } from '@/stores/friends'
import { useShare } from '@/composables/useShare'

const { initShare } = useShare()
const { t } = useI18n()
const route = useRoute()
const photos = usePhotos()
const supabase = useSupabase()
const showCommentsDrawer = ref(false)
const showReportModal = ref(false)
const friends = useFriends()

const props = defineProps<{
  photo: Photo
  hideHeader?: boolean
}>()

const emit = defineEmits<{
  like: [photoId: string]
  bookmark: [photoId: string]
  commentsUpdated: [photoId: string]
  drawerStateChanged: [isOpen: boolean]
}>()

const isFriends = computed(() => friends.isMyFriend(props.photo.user_id))

const handleCommentAdded = () => {
  // Emit event to parent to refresh photo data (for updated comment count)
  emit('commentsUpdated', props.photo.photo_id)
}

const handleCommentCountChanged = (newCount: number) => {
  // Update the local photo comment count immediately
  props.photo.comments_count = newCount
}

const handleOpenDrawer = () => {
  showCommentsDrawer.value = true
  emit('drawerStateChanged', true)
}

const handleCloseDrawer = () => {
  showCommentsDrawer.value = false
  emit('drawerStateChanged', false)
}

const handlePhotoReported = () => {
  // Photo has been reported, could potentially refresh data or show different UI
  // For now, we'll just close the modal (handled by the modal itself)
}

const share = async () => {
  initShare({
    title: t('photo.share_title'),
    text: t('photo.share_text', { username: props.photo.username }),
    url: `${window.location.origin}/photos/${props.photo.photo_id}`
  })
}
</script>

<style scoped>
.photo {
  display: grid;
  grid-template-rows: 65px 1fr auto;
  height: 100vh;
  width: 100%;
  overflow: hidden;
}

.avatar-section {
  display: flex;
  align-items: center;
  gap: theme('spacing.2');
  padding: theme('spacing.2');
}

.user-info {
  display: flex;
  flex-direction: column;
  justify-content: start;
  flex-grow: 1;
}

.photo-section {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
}

.interaction-section {
  display: flex;
  flex-direction: column;
  gap: theme('spacing.2');
  align-items: stretch;
  position: relative;
  z-index: 10;
  max-height: 25vh;
  overflow-y: auto;
  margin-top: theme('spacing.3');
  border-top: 1px solid theme('colors.stone.200');

  .dark & {
    border-top: 1px solid theme('colors.stone.700');
  }

  button {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: theme('spacing.1');
  }
}

.description-section {
  padding: theme('spacing.2');
  background: theme('colors.stone.950');
  color: theme('colors.stone.400');
  font-size: theme('fontSize.sm');
  overflow-y: auto;
}
</style>
